/**
 * PreDeparturePhaseExpanded - Expandable cards version of Phase 1
 */

import { devLog } from '../../../../../utils/devLog';
import React, { useState, useEffect } from 'react';
import { Anchor } from 'lucide-react';
import VoyageDetailsCard from './cards/VoyageDetailsCard';
import CrewSelectionCard from './cards/CrewSelectionCard';
import PassengersCard from './cards/PassengersCard';
import PreDepartureChecksCard from './cards/PreDepartureChecksCard';
import WeatherAssessmentCard from './cards/WeatherAssessmentCard';

const PreDeparturePhaseExpanded = ({ 
  activeVoyage,
  onUpdateVoyage,
  onLetGo,
  crew = [],
  passagePlans = [],
  currentVessel
}) => {
  // Expanded states for each card
  const [expanded, setExpanded] = useState({
    voyageDetails: true,
    crewSelection: false,
    passengers: false,
    preDeparture: false,
    weather: false
  });

  // Form states
  const [voyageDetails, setVoyageDetails] = useState({
    departurePort: activeVoyage?.departurePort || '',
    destinationPort: activeVoyage?.destinationPort || '',
    passagePlanId: activeVoyage?.passagePlanId || '',
    purpose: activeVoyage?.purpose || 'commercial',
    estimatedDuration: '',
    notes: activeVoyage?.notes || ''
  });

  const [selectedCrew, setSelectedCrew] = useState(
    activeVoyage?.crewList || []
  );

  const [passengers, setPassengers] = useState({
    hasPassengers: false,
    count: 0,
    names: []
  });

  const [preDepartureChecks, setPreDepartureChecks] = useState({});
  
  const [weather, setWeather] = useState({
    source: 'manual', // 'api' or 'manual'
    windSpeed: '',
    windDirection: '',
    seaState: '',
    visibility: '',
    temperature: '',
    forecast: '',
    isAcceptable: null
  });

  // Check completion status
  const isVoyageDetailsComplete = () => {
    return voyageDetails.departurePort && 
           voyageDetails.destinationPort &&
           (voyageDetails.passagePlanId || voyageDetails.purpose);
  };

  const isCrewComplete = () => {
    return selectedCrew.length > 0;
  };

  const isPreDepartureComplete = () => {
    const requiredChecks = Object.keys(preDepartureChecks).filter(key => 
      preDepartureChecks[key] !== undefined
    );
    return requiredChecks.length > 0 && 
           requiredChecks.every(key => preDepartureChecks[key] === true);
  };

  const isWeatherAssessed = () => {
    return weather.isAcceptable !== null;
  };

  const canDepart = () => {
    // Temporarily always return true for UI testing
    return true;
    
    // Full check would be:
    // return isVoyageDetailsComplete() && 
    //        isCrewComplete() && 
    //        isPreDepartureComplete() && 
    //        isWeatherAssessed();
  };

  // Toggle card expansion
  const toggleCard = (cardName) => {
    setExpanded(prev => ({
      ...prev,
      [cardName]: !prev[cardName]
    }));
  };

  // Update voyage with current data - REMOVED to prevent infinite loop
  // This was causing constant re-renders as onUpdateVoyage would trigger parent updates
  // which would pass new props, causing this effect to run again
  
  // Instead, we'll update only when user clicks "Let Go" button
  const handleLetGo = () => {
    devLog('PreDeparturePhaseExpanded: Let Go clicked');
    devLog('Active voyage:', activeVoyage);
    devLog('Can depart:', canDepart());
    
    if (onUpdateVoyage && activeVoyage) {
      const updatedVoyage = {
        ...activeVoyage,
        ...voyageDetails,
        crewList: selectedCrew,
        passengers: passengers.hasPassengers ? passengers : null,
        weatherAssessment: weather.isAcceptable !== null ? weather : null,
        preDepartureChecks: preDepartureChecks
      };
      devLog('Updating voyage with:', updatedVoyage);
      onUpdateVoyage(updatedVoyage);
    }
    
    // Then call the actual let go handler
    if (onLetGo) {
      devLog('Calling onLetGo handler');
      onLetGo();
    } else {
      devLog('No onLetGo handler provided!');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Pre-Departure Phase</h3>
          <p className="text-sm text-gray-500 mt-1">Complete all requirements before letting go</p>
        </div>
        
        <div className="p-5 space-y-4">
          {/* Voyage Details Card */}
          <VoyageDetailsCard
            voyageDetails={voyageDetails}
            setVoyageDetails={setVoyageDetails}
            passagePlans={passagePlans}
            isExpanded={expanded.voyageDetails}
            onToggle={() => toggleCard('voyageDetails')}
            isComplete={isVoyageDetailsComplete()}
          />

          {/* Crew Selection Card */}
          <CrewSelectionCard
            crew={crew}
            selectedCrew={selectedCrew}
            setSelectedCrew={setSelectedCrew}
            isExpanded={expanded.crewSelection}
            onToggle={() => toggleCard('crewSelection')}
            isComplete={isCrewComplete()}
          />

          {/* Passengers Card */}
          <PassengersCard
            passengers={passengers}
            setPassengers={setPassengers}
            isExpanded={expanded.passengers}
            onToggle={() => toggleCard('passengers')}
            isComplete={!passengers.hasPassengers || passengers.count > 0}
          />

          {/* Pre-Departure Checks Card */}
          <PreDepartureChecksCard
            preDepartureChecks={preDepartureChecks}
            setPreDepartureChecks={setPreDepartureChecks}
            hasPassengers={passengers.hasPassengers}
            isExpanded={expanded.preDeparture}
            onToggle={() => toggleCard('preDeparture')}
            isComplete={isPreDepartureComplete()}
          />

          {/* Weather Assessment Card */}
          <WeatherAssessmentCard
            weather={weather}
            setWeather={setWeather}
            isExpanded={expanded.weather}
            onToggle={() => toggleCard('weather')}
            isComplete={isWeatherAssessed()}
          />

          {/* Let Go Button */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleLetGo}
              disabled={!canDepart() || weather.isAcceptable === false}
              className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${
                canDepart() && weather.isAcceptable !== false
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Anchor className="h-5 w-5" />
              {!canDepart() ? 'Complete All Requirements' : 'Let Go - Depart'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreDeparturePhaseExpanded;